import api from './api';
import { Cost, CostAllocation, PaginatedResponse } from '../types';

export interface CostFilters {
  period_id?: number;
  farm_id?: number;
  cost_level?: 'farm' | 'box' | 'animal';
  category?: string;
  approved?: boolean;
  from_date?: string;
  to_date?: string;
  page?: number;
}

export interface CreateCostData {
  period_id?: number;
  farm_id?: number;
  box_id?: number;
  animal_id?: number;
  cost_level: 'farm' | 'box' | 'animal';
  category: string;
  description: string;
  amount: number;
  cost_date: string;
  invoice_number?: string;
  supplier?: string;
  allocation_method: 'darabszam' | 'suly' | 'egyedi';
  notes?: string;
}

export interface AllocationResult {
  success: boolean;
  allocations: CostAllocation[];
  total_allocated: number;
  animal_count: number;
}

export const costApi = {
  getCosts: async (filters?: CostFilters): Promise<PaginatedResponse<Cost>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
        }
      });
    }
    const queryString = params.toString();
    const url = queryString ? `/costs?${queryString}` : '/costs';
    const response = await api.get(url);
    return response.data;
  },

  getCost: async (id: number): Promise<Cost> => {
    const response = await api.get(`/costs/${id}`);
    return response.data;
  },

  createCost: async (data: CreateCostData): Promise<Cost> => {
    const response = await api.post('/costs', data);
    return response.data;
  },

  updateCost: async (id: number, data: Partial<CreateCostData>): Promise<Cost> => {
    const response = await api.put(`/costs/${id}`, data);
    return response.data;
  },

  deleteCost: async (id: number): Promise<void> => {
    await api.delete(`/costs/${id}`);
  },

  approveCost: async (id: number): Promise<{ message: string; cost: Cost }> => {
    const response = await api.post(`/costs/${id}/approve`);
    return response.data;
  },

  allocateCost: async (id: number): Promise<{ message: string; data: AllocationResult }> => {
    const response = await api.post(`/costs/${id}/allocate`);
    return response.data;
  },

  getAllocations: async (costId: number) => {
    const response = await api.get(`/costs/${costId}/allocations`);
    return response.data;
  },

  deleteAllocations: async (costId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/costs/${costId}/allocations`);
    return response.data;
  },

  overrideAllocation: async (allocationId: number, data: { allocated_amount: number; notes?: string }) => {
    const response = await api.patch(`/cost-allocations/${allocationId}/override`, data);
    return response.data;
  },

  revertAllocation: async (allocationId: number) => {
    const response = await api.post(`/cost-allocations/${allocationId}/revert`);
    return response.data;
  },

  getAnimalCosts: async (animalId: number) => {
    const response = await api.get(`/costs/allocation/${animalId}`);
    return response.data;
  },
};
